import {
  consolidateMultiValueTokens,
  defaultOperatorFor,
  deserializePropertyFilterQuery,
  hasPropertyFilters,
  isFilterOperator,
  KAI_TABLE_FILTERS_EVENT,
  newTokenId,
  serializePropertyFilterQuery,
  upsertPropertyFilterToken,
  type FilterOperator,
  type KaiFilterContext,
  type KaiTableFilterToken,
  type KaiTableFiltersEventDetail,
  type KaiTableFiltersPatch,
  type PropertyFilterQuery,
  type PropertyFilterToken,
} from "@/components/table";

import { BOOKING_FILTER_PROPERTIES } from "./bookingFilterProperties";

export const BOOKINGS_FILTER_STORAGE_KEY = "twido-v2-bookings-filters";

const KNOWN_KEYS = new Set([
  "search",
  ...BOOKING_FILTER_PROPERTIES.map((property) => property.key),
]);

function defaultOperator(propertyKey: string): FilterOperator {
  const property = BOOKING_FILTER_PROPERTIES.find(
    (item) => item.key === propertyKey,
  );
  if (property) return defaultOperatorFor(property);
  return ":";
}

function valueLabelFor(propertyKey: string, value: string): string | undefined {
  const property = BOOKING_FILTER_PROPERTIES.find(
    (item) => item.key === propertyKey,
  );
  return property?.options?.find((option) => option.value === value)?.label;
}

/** Build a v2 token from a Kai patch token, or null if invalid / unknown. */
export function tokenFromKaiPatch(
  raw: KaiTableFilterToken,
): PropertyFilterToken | null {
  if (!raw || typeof raw.property_key !== "string") return null;
  const propertyKey = raw.property_key.trim();
  if (!KNOWN_KEYS.has(propertyKey)) return null;
  if (raw.value == null || String(raw.value).trim() === "") return null;

  const requested = raw.operator?.trim() ?? "";
  const operator = isFilterOperator(requested)
    ? requested
    : defaultOperator(propertyKey);
  const value = String(raw.value);

  return {
    id: newTokenId(),
    propertyKey: propertyKey === "search" ? undefined : propertyKey,
    operator,
    value,
    valueLabel: valueLabelFor(propertyKey, value),
  };
}

export function patchToTokens(patch: KaiTableFiltersPatch): PropertyFilterToken[] {
  const tokens: PropertyFilterToken[] = [];
  for (const raw of patch.tokens ?? []) {
    const token = tokenFromKaiPatch(raw);
    if (token) tokens.push(token);
  }
  // v2's filter bar is flat (no nested groups). Flatten Kai groups so OR/AND
  // clusters still contribute their tokens; top-level `operation` is honored
  // separately.
  for (const group of patch.token_groups ?? []) {
    for (const raw of group.tokens ?? []) {
      const token = tokenFromKaiPatch(raw);
      if (token) tokens.push(token);
    }
  }
  return tokens;
}

export function applyKaiFiltersPatchToQuery(
  current: PropertyFilterQuery,
  patch: KaiTableFiltersPatch,
): PropertyFilterQuery {
  const mode = patch.mode ?? "merge";
  const operation: PropertyFilterQuery["operation"] =
    patch.operation === "or" || patch.operation === "and"
      ? patch.operation
      : current.operation ?? "and";

  let tokens: PropertyFilterToken[];
  if (mode === "clear") {
    tokens = [];
  } else if (mode === "replace") {
    tokens = patchToTokens(patch);
  } else {
    tokens = [...current.tokens];
    for (const incoming of patchToTokens(patch)) {
      tokens = upsertPropertyFilterToken(tokens, incoming, BOOKING_FILTER_PROPERTIES);
    }
  }

  return consolidateMultiValueTokens({ operation, tokens }, BOOKING_FILTER_PROPERTIES);
}

export function loadBookingsFilterQuery(): PropertyFilterQuery {
  try {
    return deserializePropertyFilterQuery(
      sessionStorage.getItem(BOOKINGS_FILTER_STORAGE_KEY),
    );
  } catch {
    return { operation: "and", tokens: [] };
  }
}

export function persistBookingsFilterQuery(query: PropertyFilterQuery) {
  try {
    if (!hasPropertyFilters(query)) {
      sessionStorage.removeItem(BOOKINGS_FILTER_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(
      BOOKINGS_FILTER_STORAGE_KEY,
      serializePropertyFilterQuery(query),
    );
  } catch {
    // Ignore quota / private-mode failures; the window event still applies.
  }
}

/** Snapshot of the current bookings filters for Kai's client context. */
export function buildBookingsFilterContextSnapshot(
  query: PropertyFilterQuery = loadBookingsFilterQuery(),
): KaiFilterContext {
  return {
    page: "bookings",
    operation: query.operation === "or" ? "or" : "and",
    active_tokens: query.tokens
      .filter((token) => token.value.trim() !== "")
      .map((token) => ({
        property_key: token.propertyKey ?? "search",
        operator: token.operator,
        value: token.value,
      })),
  };
}

/**
 * Merge a Kai filters patch into the stored bookings query, persist, and
 * notify any mounted table via a window event.
 */
export function applyKaiBookingsTableFilters(
  patch: KaiTableFiltersPatch,
): PropertyFilterQuery {
  const next = applyKaiFiltersPatchToQuery(loadBookingsFilterQuery(), patch);
  persistBookingsFilterQuery(next);
  window.dispatchEvent(
    new CustomEvent<KaiTableFiltersEventDetail>(KAI_TABLE_FILTERS_EVENT, {
      detail: {
        page: "bookings",
        storageKey: BOOKINGS_FILTER_STORAGE_KEY,
        query: next,
      },
    }),
  );
  return next;
}
